import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  canSeeModule as rbacCanSeeModule,
  hasAction as rbacHasAction,
  hasStaffAccess as rbacHasStaffAccess,
  isStrictAdmin as rbacIsStrictAdmin,
  showSelfServiceNav,
} from "@/lib/rbac";
import {
  Users,
  LayoutDashboard,
  Building2,
  Briefcase,
  LogOut,
  ChevronRight,
  ChevronDown,
  Building,
  CalendarDays,
  Wallet,
  User,
  ListChecks,
  FileText,
  ClipboardCheck,
  Clock,
  CalendarHeart,
  FileSignature,
  IdCard,
  BarChart3,
  KeyRound,
  ShieldCheck,
  Laptop,
  Inbox,
  Plus,
  Upload,
  Download,
  Eye,
  Cog,
  MapPin,
  UserCheck,
  UserPlus,
  ClipboardList,
  TrendingUp,
  Target,
  Award,
  Banknote,
  FileBarChart,
  Calendar,
  Repeat,
  Hash,
  IndianRupee,
  ShoppingCart,
  Receipt,
  Stamp,
} from "lucide-react";

const SectionLabel = ({ children }) => (
  <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
    {children}
  </div>
);

const FlatItem = ({ to, icon: Icon, label, active, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`flex items-center gap-3 px-4 py-2.5 text-sm transition border-l-[3px] ${
      active
        ? "border-[#226DB4] bg-[#1A4A7A] text-white font-semibold"
        : "border-transparent text-slate-300 hover:bg-[#152536] hover:text-white"
    }`}
  >
    <Icon className={`h-4 w-4 ${active ? "text-[#7EB6E8]" : ""}`} />
    <span className="font-medium">{label}</span>
  </Link>
);

const GroupHeader = ({ icon: Icon, label, open, onToggle, anyChildActive, testid }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testid}
    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition border-l-[3px] ${
      anyChildActive
        ? "border-[#226DB4] bg-[#1A4A7A] text-white font-semibold"
        : "border-transparent text-slate-300 hover:bg-[#152536] hover:text-white"
    }`}
  >
    <Icon className={`h-4 w-4 ${anyChildActive ? "text-[#7EB6E8]" : ""}`} />
    <span className="font-medium flex-1 text-left">{label}</span>
    <ChevronDown
      className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
    />
  </button>
);

const SubItem = ({ to, icon: Icon, label, active, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`flex items-center gap-2.5 pl-11 pr-4 py-2 text-[13px] transition border-l-[3px] ${
      active
        ? "border-[#226DB4] bg-[#1A4A7A] text-white font-semibold"
        : "border-transparent text-slate-400 hover:bg-[#152536] hover:text-white"
    }`}
  >
    {Icon ? (
      <Icon className={`h-3.5 w-3.5 ${active ? "text-[#7EB6E8]" : ""}`} />
    ) : (
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-[#7EB6E8]" : "bg-slate-500"}`} />
    )}
    <span>{label}</span>
  </Link>
);

export default function AdminLayout({ children, title, breadcrumbs = [], accent }) {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isStrictAdmin = rbacIsStrictAdmin(user);
  const isStaff = rbacHasStaffAccess(user);
  const selfService = showSelfServiceNav(user);

  // Refresh effective perms when returning to the app (role edits take effect)
  useEffect(() => {
    if (!user) return;
    const onFocus = () => { refreshUser?.().catch(() => {}); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const hasAction = (module, action) => rbacHasAction(user, module, action);
  const canSeeModule = (module, actions) => rbacCanSeeModule(user, module, actions);

  const onLogout = async () => {
    try { await api.post("/auth/logout").catch(() => {}); } catch (e) { /* ignore */ }
    logout();
    navigate("/login", { replace: true });
  };

  const isActive = (p) => location.pathname.startsWith(p);

  const essGroups = useMemo(() => {
    const groups = [
      {
        id: "ess-profile",
        icon: User,
        label: "My Info",
        items: [
          { to: "/me/profile", label: "My Profile", icon: User },
          { to: "/me/letters", label: "My Letters", icon: FileSignature },
        ],
      },
      {
        id: "ess-time",
        icon: Clock,
        label: "Time & Attendance",
        items: [
          { to: "/me/attendance", label: "My Attendance", icon: Clock },
          { to: "/me/regularization", label: "Regularization", icon: Repeat },
        ],
      },
      {
        id: "ess-leave",
        icon: CalendarDays,
        label: "Leave & Calendar",
        items: [
          { to: "/me/leaves", label: "My Leaves", icon: CalendarDays },
          { to: "/me/team-leave", label: "Team Leave Calendar", icon: Calendar },
          ...(!isStaff
            ? [{ to: "/holidays", label: "Holiday Calendar", icon: CalendarHeart }]
            : []),
        ],
      },
      {
        id: "ess-pay",
        icon: Wallet,
        label: "Pay & Tax",
        items: [
          ...(user?.employment_type === "Full-time"
            ? [{ to: "/me/payslips", label: "My Payslips", icon: FileText }]
            : []),
          { to: "/me/tax", label: "Form 16 / Tax", icon: FileBarChart },
        ],
      },
    ];
    return groups.filter((g) => g.items.length > 0);
  }, [user?.employment_type, isStaff]);

  const essGroupForPath = (pathname) => {
    for (const g of essGroups) {
      for (const it of g.items) {
        if (pathname === it.to || pathname.startsWith(it.to + "/")) return g.id;
      }
    }
    return null;
  };
  const [openEssGroup, setOpenEssGroup] = useState(() => essGroupForPath(location.pathname));
  useEffect(() => {
    const g = essGroupForPath(location.pathname);
    if (g) setOpenEssGroup(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, essGroups.length]);

  const groups = useMemo(() => {
    // Driven by effective multi-role permissions — not primary role alone
    return [
      {
        id: "organization",
        icon: Building2,
        label: "Organization",
        show: canSeeModule("departments") || canSeeModule("designations") || canSeeModule("assets") || canSeeModule("locations") || canSeeModule("employee_ids") || (isStaff && canSeeModule("holidays")) || isStrictAdmin,
        items: [
          { to: "/departments",  label: "Departments",        icon: Building2, show: canSeeModule("departments") },
          { to: "/designations", label: "Designations",       icon: Briefcase, show: canSeeModule("designations") },
          { to: "/locations",    label: "Locations / Branches", icon: MapPin,  show: canSeeModule("locations") || isStrictAdmin },
          { to: "/organization/employee-ids", label: "Employee ID Prefixes", icon: Hash, show: canSeeModule("employee_ids") || isStrictAdmin },
          { to: "/assets",       label: "Inventory (Assets)", icon: Laptop,    show: canSeeModule("assets") },
          // Staff/admin only here — employees use Self Service → Holiday Calendar
          { to: "/holidays",     label: "Holiday Calendar",   icon: CalendarHeart, show: isStaff && (canSeeModule("holidays") || isStrictAdmin) },
        ],
      },
      {
        id: "prehire",
        icon: UserPlus,
        label: "Pre-hire",
        show: canSeeModule("candidates"),
        items: [
          { to: "/candidates", label: "Candidates", icon: Eye, show: hasAction("candidates", "view") },
          { to: "/candidates?action=add", label: "Add Candidate", icon: Plus, show: hasAction("candidates", "add") },
          { to: "/letters?action=generate&for=candidate", label: "Issue Offer Letter", icon: FileSignature, show: hasAction("letters", "add") },
        ],
      },
      {
        id: "employees",
        icon: Users,
        label: "Employees",
        show: canSeeModule("employees"),
        items: [
          { to: "/employees", label: "Employee Directory", icon: Eye, show: hasAction("employees", "view") },
          { to: "/employees?action=add", label: "Add Employee", icon: Plus, show: hasAction("employees", "add") },
          { to: "/employees?action=bulk-upload", label: "Bulk Upload", icon: Upload, show: hasAction("employees", "add") },
          { to: "/employees?tab=Intern", label: "Convert Intern", icon: UserCheck, show: hasAction("employees", "edit") },
        ],
      },
      {
        id: "attendance",
        icon: ClipboardCheck,
        label: "Attendance",
        show: canSeeModule("attendance"),
        items: [
          { to: "/attendance", label: "Daily Attendance", icon: ClipboardCheck, show: hasAction("attendance", "view") },
          { to: "/attendance?tab=summary", label: "Monthly Summary", icon: CalendarDays, show: hasAction("attendance", "view") },
          { to: "/attendance/regularizations", label: "Regularization Requests", icon: Repeat, show: hasAction("attendance", "edit") },
          { to: "/attendance?action=bulk-upload", label: "Bulk Upload", icon: Upload, show: hasAction("attendance", "add") },
        ],
      },
      {
        id: "leave",
        icon: CalendarDays,
        label: "Leave",
        show: canSeeModule("leaves"),
        items: [
          { to: "/leaves",          label: "Leave Dashboard",   icon: LayoutDashboard, show: hasAction("leaves", "view") },
          { to: "/leaves?action=apply", label: "Apply Leave",   icon: Plus,            show: hasAction("leaves", "add") || true },
          { to: "/leaves?status=Pending", label: "Leave Approvals", icon: ClipboardList, show: hasAction("leaves", "approve") || hasAction("leaves", "view") },
          { to: "/leave-calendar",  label: "Leave Calendar",    icon: Calendar,        show: hasAction("leaves", "view") },
          { to: "/leave-types",     label: "Leave Types",       icon: ListChecks,      show: hasAction("leaves", "view") },
        ],
      },
      {
        id: "payroll",
        icon: Wallet,
        label: "Payroll",
        show: canSeeModule("payroll"),
        items: [
          { to: "/payroll/wizard",             label: "Generate Payroll",  icon: Repeat,    show: hasAction("payroll", "add") },
          { to: "/payroll?action=generate",    label: "Generate Single",   icon: Plus,      show: hasAction("payroll", "add") },
          { to: "/payroll",                    label: "Payslips",          icon: FileText,  show: hasAction("payroll", "view") },
          { to: "/payroll/tax",                label: "Tax & Form 16",     icon: FileBarChart, show: hasAction("payroll", "view") || hasAction("statutory", "view") || hasAction("statutory", "export") },
          { to: "/payroll/statutory",          label: "Statutory reports", icon: FileBarChart, show: hasAction("statutory", "export") || hasAction("payroll", "export") },
        ],
      },
      {
        id: "letters",
        icon: FileSignature,
        label: "Letters",
        show: canSeeModule("letters"),
        items: [
          { to: "/letters",                  label: "All Letters",     icon: Eye,  show: hasAction("letters", "view") },
          { to: "/letters?action=generate",  label: "Generate Letter", icon: Plus, show: hasAction("letters", "add") },
        ],
      },
      {
        id: "finance",
        icon: IndianRupee,
        label: "Finance",
        show: canSeeModule("finance_po") || canSeeModule("finance_invoices") || isStrictAdmin,
        items: [
          { to: "/finance/po", label: "Purchase Orders", icon: ShoppingCart, show: canSeeModule("finance_po") || isStrictAdmin },
          { to: "/finance/invoices", label: "Invoices", icon: Receipt, show: canSeeModule("finance_invoices") || isStrictAdmin },
        ],
      },
      {
        id: "performance",
        icon: TrendingUp,
        label: "Performance",
        show: canSeeModule("salary_revisions") || canSeeModule("performance") || isStrictAdmin,
        items: [
          { to: "/salary-revisions", label: "Salary Revisions", icon: Banknote, show: canSeeModule("salary_revisions") || isStrictAdmin },
          { to: "/performance/reviews", label: "Performance Reviews", icon: Award,  show: canSeeModule("performance") || isStrictAdmin },
          { to: "/performance/goals",   label: "Goals / KPI Setup",   icon: Target, show: canSeeModule("performance") || isStrictAdmin },
          { to: "/performance/cycles",  label: "Appraisal Cycles",    icon: Repeat, show: canSeeModule("performance") || isStrictAdmin },
        ],
      },
      {
        id: "reports",
        icon: BarChart3,
        label: "Reports",
        show: canSeeModule("reports") || canSeeModule("employees") || canSeeModule("attendance") || canSeeModule("leaves") || canSeeModule("payroll"),
        items: [
          { to: "/reports/employees",  label: "Employee Reports",   icon: Users,         show: (canSeeModule("reports") || canSeeModule("employees")) && canSeeModule("employees") },
          { to: "/reports/bank-details", label: "Bank Details",     icon: Banknote,      show: (canSeeModule("reports") || canSeeModule("employees") || canSeeModule("payroll")) && canSeeModule("employees") },
          { to: "/reports/headcount",  label: "Headcount",          icon: BarChart3,     show: (canSeeModule("reports") || canSeeModule("employees")) && canSeeModule("employees") },
          { to: "/reports/separations", label: "Separation Report",  icon: LogOut,        show: (canSeeModule("reports") || canSeeModule("employees")) && canSeeModule("employees") },
          { to: "/reports/attendance", label: "Attendance Reports", icon: ClipboardCheck, show: (canSeeModule("reports") || canSeeModule("attendance")) && canSeeModule("attendance") },
          { to: "/leave-reports",      label: "Leave Reports",      icon: CalendarDays,  show: (canSeeModule("reports") || canSeeModule("leaves")) && canSeeModule("leaves") },
          { to: "/reports/payroll",    label: "Payroll Reports",    icon: Wallet,        show: (canSeeModule("reports") || canSeeModule("payroll")) && canSeeModule("payroll") },
        ],
      },
      {
        id: "settings",
        icon: Cog,
        label: "Settings",
        show:
          isStrictAdmin ||
          canSeeModule("users") ||
          canSeeModule("roles") ||
          canSeeModule("companies") ||
          canSeeModule("letter_templates") ||
          canSeeModule("document_approvals"),
        items: [
          { to: "/users",            label: "User Management",     icon: ShieldCheck,  show: canSeeModule("users") || isStrictAdmin },
          { to: "/roles",            label: "Roles & Permissions", icon: KeyRound,     show: canSeeModule("roles") || isStrictAdmin },
          { to: "/companies",        label: "Companies",           icon: Building,     show: canSeeModule("companies") || isStrictAdmin },
          { to: "/letter-templates", label: "Letter Templates",    icon: FileSignature, show: canSeeModule("letter_templates") || isStrictAdmin },
          { to: "/settings/document-approvals", label: "Document Approvals", icon: Stamp, show: isStrictAdmin || canSeeModule("document_approvals") },
          { to: "/id-card-templates", label: "ID Card Templates",  icon: IdCard,       show: canSeeModule("id_cards") || isStrictAdmin },
          { to: "/settings/system",  label: "System Settings",     icon: Cog,          show: isStrictAdmin },
        ],
      },
    ]
      .map((g) => ({ ...g, items: g.items.filter((i) => i.show) }))
      .filter((g) => g.show && g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isStrictAdmin, isStaff]);

  const groupForPath = (pathname) => {
    for (const g of groups) {
      for (const it of g.items) {
        const target = it.to.split("?")[0];
        if (pathname === target || pathname.startsWith(target + "/")) return g.id;
      }
    }
    return null;
  };
  const [openGroup, setOpenGroup] = useState(() => groupForPath(location.pathname));
  useEffect(() => {
    const g = groupForPath(location.pathname);
    if (g) setOpenGroup(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, groups.length]);
  const toggleGroup = (id) => setOpenGroup((cur) => (cur === id ? null : id));

  /** Score how well a nav target matches the current URL. Highest score wins (avoids /payroll highlighting on /payroll/wizard). */
  const navMatchScore = (to) => {
    const [path, query] = String(to).split("?");
    const { pathname, search } = location;
    const currentParams = new URLSearchParams(search);

    if (query) {
      if (pathname !== path) return -1;
      const wantedParams = new URLSearchParams(query);
      let paramHits = 0;
      for (const [k, v] of wantedParams.entries()) {
        if (currentParams.get(k) !== v) return -1;
        paramHits += 1;
      }
      // Prefer the more specific query match (e.g. action+for over action alone)
      return 1000 + path.length + paramHits * 50;
    }

    if (pathname === path) {
      // Bare path loses when a sibling query-nav owns action/tab/status/for
      if (currentParams.get("action") || currentParams.get("tab") || currentParams.get("status") || currentParams.get("for")) {
        return -1;
      }
      return 500 + path.length;
    }

    // Detail routes e.g. /employees/:id → highlight directory, not /payroll → /payroll/wizard
    if (pathname.startsWith(path + "/")) return path.length;
    return -1;
  };

  const allNavTargets = useMemo(
    () => groups.flatMap((g) => g.items.map((it) => it.to)),
    [groups]
  );

  const subItemActive = (to) => {
    const score = navMatchScore(to);
    if (score < 0) return false;
    let best = score;
    for (const t of allNavTargets) {
      const s = navMatchScore(t);
      if (s > best) best = s;
    }
    return score === best;
  };

  return (
    <div className="min-h-screen flex bg-[#F1F5F9]">
      <aside className="w-64 bg-[#0F1C2E] flex-shrink-0 flex flex-col">
        <div className="h-14 flex items-center px-5 bg-[#0B1524] border-b border-[#0B1524]">
          <div className="h-8 w-8 rounded-md bg-[#226DB4] text-white flex items-center justify-center font-bold mr-2">M</div>
          <div className="text-white font-semibold tracking-wide">Myspace - Spherical</div>
        </div>

        <div className="px-4 py-4 border-b border-[#0B1524]">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-[#226DB4] text-white flex items-center justify-center font-semibold uppercase">
              {(user?.name || "U").charAt(0)}
            </div>
            <div className="text-sm min-w-0">
              <div className="text-white font-medium truncate">{user?.name || "User"}</div>
              <div className="text-emerald-400 text-xs flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                <span className="capitalize">{user?.role || "employee"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pb-4">
          <SectionLabel>Main</SectionLabel>
          <FlatItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" active={isActive("/dashboard")} testid="nav-dashboard" />
          <FlatItem to="/approvals" icon={Inbox} label="My Approvals" active={isActive("/approvals")} testid="nav-approvals" />

          {groups.length > 0 && (
            <>
              <SectionLabel>Modules</SectionLabel>
              {groups.map((g) => {
                const anyChildActive = g.items.some((it) => subItemActive(it.to));
                const isOpen = openGroup === g.id || anyChildActive;
                return (
                  <div key={g.id} data-testid={`nav-group-${g.id}`}>
                    <GroupHeader
                      icon={g.icon}
                      label={g.label}
                      open={isOpen}
                      onToggle={() => toggleGroup(g.id)}
                      anyChildActive={anyChildActive}
                      testid={`nav-group-${g.id}-toggle`}
                    />
                    {isOpen && (
                      <div className="bg-[#132030] py-1" data-testid={`nav-group-${g.id}-items`}>
                        {g.items.map((it) => (
                          <SubItem
                            key={it.to}
                            to={it.to}
                            icon={it.icon}
                            label={it.label}
                            active={subItemActive(it.to)}
                            testid={`nav-${g.id}-${slug(it.label)}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {selfService && (
            <>
              <SectionLabel>Self Service</SectionLabel>
              {essGroups.map((g) => {
                const anyChildActive = g.items.some((it) => isActive(it.to));
                const isOpen = openEssGroup === g.id || anyChildActive;
                return (
                  <div key={g.id}>
                    <GroupHeader
                      icon={g.icon}
                      label={g.label}
                      open={isOpen}
                      onToggle={() => setOpenEssGroup((cur) => (cur === g.id ? null : g.id))}
                      anyChildActive={anyChildActive}
                      testid={`nav-ess-${g.id}`}
                    />
                    {isOpen && (
                      <div className="bg-[#132030] py-1" data-testid={`nav-ess-${g.id}-items`}>
                        {g.items.map((it) => (
                          <SubItem
                            key={it.to}
                            to={it.to}
                            icon={it.icon}
                            label={it.label}
                            active={isActive(it.to)}
                            testid={`nav-ess-${slug(it.label)}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          <SectionLabel>Account</SectionLabel>
          <FlatItem to="/account/password" icon={KeyRound} label="Change Password" active={isActive("/account/password")} testid="nav-change-password" />
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header
          className={`h-14 border-b flex items-center justify-between px-6 shadow-sm gap-4 ${
            accent === "prehire"
              ? "bg-sky-50/90 border-sky-100"
              : "bg-white border-slate-200"
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {accent === "prehire" && (
              <span
                className="shrink-0 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-sky-100 text-sky-800 border border-sky-200"
                data-testid="module-accent-prehire"
              >
                Pre-hire
              </span>
            )}
            <h1 className="text-lg font-semibold text-slate-700 truncate" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-sm text-slate-500 hidden md:flex items-center gap-1.5">
              {breadcrumbs.map((b, i) => (
                <span key={`${b}-${i}`} className="flex items-center gap-1.5">
                  {i > 0 && <ChevronRight className="h-3.5 w-3.5" />}
                  <span className={i === breadcrumbs.length - 1 ? "text-slate-700 font-medium" : ""}>{b}</span>
                </span>
              ))}
            </div>
            <button
              type="button"
              data-testid="logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-rose-600 border border-rose-700 rounded hover:bg-rose-700"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">{children}</main>
      </div>
    </div>
  );
}

function slug(s) {
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
